import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UniversalMediaList } from '@/app/features/universal-media-lists/models';
import { UniversalMediaListsService } from '@/app/features/universal-media-lists/_services/universal-media-lists.service';
import { AppFilter, AppTable, AppTableColumn, CommonDataModel, ViewMode } from '../../_models/models';
import { SharedService } from '../../_services/shared.service';
import {
  UniversalMediaListSegmentType,
  UniversalMediaListSegmentTypeDisplayName,
  UniversalMediaListType,
  UniversalMediaListTypeDisplayName
} from '@/app/features/universal-media-lists/universal-media-list-types.model';
import { VIEW_BUTTON_RULES } from './universal-media-domains.config';

@Component({
  selector: 'app-universal-media-domains',
  templateUrl: './universal-media-domains.component.html',
  styleUrls: ['./universal-media-domains.component.less']
})
export class UniversalMediaDomainsComponent implements OnDestroy {
  @Input() universalMediaList: UniversalMediaList;
  @Input() universalMediaMediaType: string;
  @Input() mode: 'select' | 'view' | 'standalone' = 'select';
  @Input() status: 'selected' | 'disabled' = null;
  @Input() isInternalUser: boolean;
  @Input() rxDealMode: boolean;
  @Output() selected = new EventEmitter<UniversalMediaList>();
  @Output() viewRequested = new EventEmitter<UniversalMediaList>();

  isExpanded = false;

  viewMode: ViewMode = ViewMode.SELECTION;
  private selectedUniversalMediaListForView: UniversalMediaList = null;

  universalMediaContentList: any[] = [];
  universalMediaContentMetaData: CommonDataModel<any[]>;
  universalMediaContentFilters: AppFilter[] = [];
  universalMediaContentTable: AppTable;
  universalMediaContentTableColumns: AppTableColumn[] = [];

  private unsubscribe$ = new Subject<void>();
  private universalMediaListsService = inject(UniversalMediaListsService);
  private sharedService = inject(SharedService);

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onUniversalMediaListClick(): void {
    if (this.status === 'disabled') {
      return;
    }
    this.onSelect(this.universalMediaList);
  }

  getUniversalMediaListMediaTypeNameDisplay(): string {
    if (!this.universalMediaMediaType) {
      return '';
    }

    const mediaTypeKey = Object.keys(UniversalMediaListSegmentType).find(
      key => UniversalMediaListSegmentType[key] === this.universalMediaMediaType
    ) as keyof typeof UniversalMediaListSegmentTypeDisplayName;

    return UniversalMediaListSegmentTypeDisplayName[mediaTypeKey];
  }

  getUniversalMediaListStatusDisplay(): string {
    if (!this.universalMediaList) {
      return '';
    }

    const listTypeKey = Object.keys(UniversalMediaListType).find(
      key => UniversalMediaListType[key] === this.universalMediaList.listType
    ) as keyof typeof UniversalMediaListTypeDisplayName;

    return UniversalMediaListTypeDisplayName[listTypeKey];
  }


  onSelect(universalMediaList: UniversalMediaList): void {
    this.selected.emit(universalMediaList);
  }

  shouldShowViewButton(): boolean {
    if (!this.universalMediaList) return false;

    const mediaType = this.universalMediaList.mediaType?.toLowerCase();
    const listType = this.universalMediaList.listType?.toLowerCase();

    const viewCondsForInternalUser = VIEW_BUTTON_RULES.internalUserListTypes
      .includes(listType as UniversalMediaListType);

    const viewCondsForExternalUser = VIEW_BUTTON_RULES.externalUserListTypes
      .includes(listType as UniversalMediaListType) &&
        VIEW_BUTTON_RULES.externalUserMediaTypes
          .includes(mediaType as UniversalMediaListSegmentType);

    return this.isInternalUser ? viewCondsForInternalUser : viewCondsForExternalUser;
  }

  onViewMedia(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (!this.universalMediaList) return;

    this.viewRequested.emit(this.universalMediaList);
  }

  backToSelection(): void {
    this.viewMode = ViewMode.SELECTION;
    this.selectedUniversalMediaListForView = null;
    this.universalMediaContentList = [];
    this.universalMediaContentMetaData = new CommonDataModel<any[]>([], 0, true, false, false, null);
  }

  toggleExpansion(): void {
    this.isExpanded = !this.isExpanded;
  }


  private loadUniversalMediaContent(searchTerm: string = '', pageIndex: number = 1): void {
    if (!this.selectedUniversalMediaListForView) return;

    this.universalMediaContentMetaData.isLoading = true;

    const requestParams = {
      page: pageIndex,
      limit: this.universalMediaContentTable.pageSize,
      ...(searchTerm && {
        search: { matchingValue: { $contL: `%${searchTerm}%` } }
      })
    };

    this.universalMediaListsService.getUniversalMediaListDomains(
      this.selectedUniversalMediaListForView.id,
      requestParams
    ).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (response) => {
        this.universalMediaContentList = response.data || [];
        this.universalMediaContentMetaData = new CommonDataModel<any[]>(
          this.universalMediaContentList,
          response.totalElements || 0,
          false,
          response.isPaginated || false,
          false,
          null
        );
      },
      error: (error) => {
        this.sharedService.showNotification('error', 'Error', 'Failed to load media content. Please try again.');
        this.universalMediaContentMetaData = new CommonDataModel<any[]>([], 0, false, false, false, error);
        this.universalMediaContentList = [];
      }
    });
  }

  onUniversalMediaContentSearch(event: { filters: AppFilter[]; filterId: string }): void {
    const searchFilter = event.filters?.find(f => f.id === 'mediaContentSearch');
    const searchTerm = searchFilter?.selectedValues?.[0]?.value || '';
    this.universalMediaContentTable.pageIndex = 1;
    this.loadUniversalMediaContent(searchTerm);
  }

  onUniversalMediaContentPageChanged(pageIndex: number): void {
    this.universalMediaContentTable.pageIndex = pageIndex;
    const searchFilter = this.universalMediaContentFilters?.find(f => f.id === 'mediaContentSearch');
    const searchTerm = searchFilter?.selectedValues?.[0]?.value || '';
    this.loadUniversalMediaContent(searchTerm, pageIndex);
  }

  isInViewMode(): boolean {
    return this.viewMode === ViewMode.VIEW;
  }

  isInSelectionMode(): boolean {
    return this.viewMode === ViewMode.SELECTION;
  }

  getSelectedUniversalMediaListForView(): UniversalMediaList {
    return this.selectedUniversalMediaListForView;
  }

  getUniversalMediaContentList(): any[] {
    return this.universalMediaContentList;
  }

  getUniversalMediaContentMetaData(): CommonDataModel<any[]> {
    return this.universalMediaContentMetaData;
  }

  getUniversalMediaContentFilters(): AppFilter[] {
    return this.universalMediaContentFilters;
  }

  getUniversalMediaContentTable(): AppTable {
    return this.universalMediaContentTable;
  }

  getUniversalMediaContentTableColumns(): AppTableColumn[] {
    return this.universalMediaContentTableColumns;
  }
}
